import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy} from "@angular/core";
import {CALENDAR_TYPES, TIntCalendarOutput} from "../../calendars";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {BehaviorSubject, Observable} from "rxjs";
import {KEYBOARD_KEY_PRESSED} from "../../../providers";
import {DestroyStreamService} from "../../../services";
import {takeUntil} from "rxjs/operators";
import {ClearableKeys} from "../../../enums";

@Component({
  selector: 'int-datepicker',
  templateUrl: 'int-datepicker.component.html',
  styleUrls: ['./int-datepicker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DestroyStreamService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: IntDatepickerComponent,
      multi: true
    }
  ]
})
export class IntDatepickerComponent implements ControlValueAccessor, OnDestroy {

  @Input() placeholder: string | null = '';
  @Input() type: keyof typeof CALENDAR_TYPES = 'single';
  @Input() clearable = true;

  displayedData$: BehaviorSubject<string | null | undefined>
    = new BehaviorSubject<string | null | undefined>(null);

  isFocus$: BehaviorSubject<boolean>
    = new BehaviorSubject<boolean>(false);

  date: Date | null | [Date | null, Date | null] = null;

  isShow = false;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly destroy$: DestroyStreamService,
    @Inject(KEYBOARD_KEY_PRESSED) private readonly pressService: Observable<ClearableKeys>
  ) {
    this.pressService.pipe(takeUntil(this.destroy$)).subscribe((key) => {
      if(this.isFocus$.value && [ClearableKeys.DELETE, ClearableKeys.BACKSPACE].includes(key)) {
        this.onClear();
      }
    })
  }

  get isActive() {
    return Boolean(this.displayedData$.value) || this.isFocus$.value || this.isShow;
  }

  setDisplayed(payload: Date | [Date | null, Date | null] | null) {
    const output = Array.isArray(payload)
      ? payload.map(d => d?.toLocaleDateString()).join(' - ')
      : payload?.toLocaleDateString();
    this.displayedData$.next(output ?? '');
  }

  writeValue(payload: Date | [Date | null | string, Date | null | string] | null): void {
    //@ts-ignore
    this.date = Array.isArray(payload)
      ? payload.map((value) => {
        return typeof value === "string" && Date.parse(value) ? new Date(value) : value;
      })
      : typeof payload === 'string' ? new Date(payload) : payload;
    this.setDisplayed(this.date);
  }

  onCalendarChange(payload: TIntCalendarOutput | null) {
    this.setDisplayed(payload);
    this.setData(payload);
    this.isShow = false;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onFocus() {
    this.isFocus$.next(true);
    this.onTouched();
  }
  onBlur() {
    this.isFocus$.next(false);
  }

  onContainerClick() {
    this.isShow = true;
  }

  clickOutSide() {
    this.isShow = false;
    this.cdr.markForCheck();
  }

  setData(payload: Date | [Date | null, Date | null] | null) {
    const output = payload ? (Array.isArray(payload) ? payload : [payload, null]) : null;
    this.onChange(output);
    this.date = payload;
  }

  onChange(payload: Array<Date | null> | null) {}

  onClear(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.onCalendarChange(null);
    this.isFocus$.next(false);
  }

  onTouched() {}

  ngOnDestroy() {
    this.isShow = false;
    this.isFocus$.next(false);
    this.cdr.markForCheck();
  }
}
